import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase, db } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'

const inscripcionSchema = z.object({
  usuario_nombre: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  usuario_email: z.string().email('Ingrese un email válido'),
  usuario_whatsapp: z.string().min(9, 'Ingrese un número de WhatsApp válido'),
})

export default function EventoDetalle() {
  const { id } = useParams()
  const { verifyRecaptcha } = useRecaptcha()
  const [evento, setEvento] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [faqs, setFaqs] = useState([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(inscripcionSchema)
  })

  useEffect(() => {
    fetchEvento()
    fetchFAQs()
  }, [id])

  const fetchEvento = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('eventos')
        .select(`
          *,
          categorias(nombre),
          eventos_tiendas(
            tiendas(
              id,
              nombre,
              logo_url,
              publicado
            )
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error
      setEvento(data)
    } catch (error) {
      console.error('Error fetching evento:', error)
      setError('No se pudo cargar el evento')
    } finally {
      setLoading(false)
    }
  }

  const fetchFAQs = async () => {
    try {
      const { data, error } = await db.getFAQsByScope('evento', id)
      if (error) throw error
      setFaqs(data || [])
    } catch (error) {
      console.error('Error fetching FAQs:', error)
    }
  }

  const onSubmit = async (data) => {
    setSubmitting(true)
    setError(null)

    try {
      // Verify reCAPTCHA
      const recaptchaResult = await verifyRecaptcha('inscripcion_evento')
      if (!recaptchaResult.success) {
        throw new Error('Error de verificación. Por favor intenta de nuevo.')
      }

      // Verificar si ya está inscrito
      const { data: existing } = await supabase
        .from('inscripciones_eventos')
        .select('id')
        .eq('evento_id', id)
        .eq('usuario_email', data.usuario_email)
        .single()

      if (existing) {
        setError('Ya estás inscrito en este evento')
        setSubmitting(false)
        return
      }

      // Crear inscripción
      const { error: insertError } = await supabase
        .from('inscripciones_eventos')
        .insert([{
          evento_id: id,
          ...data
        }])

      if (insertError) throw insertError

      setSuccess(true)
      reset()
      setTimeout(() => setSuccess(false), 5000)
    } catch (err) {
      console.error('Error al inscribirse:', err)
      setError(err.message || 'Ocurrió un error al inscribirse. Por favor, intente nuevamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const getEstadoBadgeColor = (estado) => {
    switch (estado) {
      case 'Activo':
        return { bg: '#D1FAE5', color: '#065F46' }
      case 'Próximo':
        return { bg: '#DBEAFE', color: '#1E40AF' }
      case 'Finalizado':
        return { bg: '#F3F4F6', color: '#374151' }
      default:
        return { bg: '#F3F4F6', color: '#374151' }
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('es-PE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  if (loading) {
    return (
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '0 2rem',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem 0'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '3px solid var(--color-accent-gray)',
              borderTop: '3px solid var(--color-text-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{
              fontFamily: 'var(--font-ui)',
              color: 'var(--color-text-muted)'
            }}>Cargando evento...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!evento) {
    return (
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '0 2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
          border: '1px solid #FCA5A5',
          borderRadius: 'var(--radius-md)',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--font-size-display-l)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            margin: '0 0 0.5rem 0',
            letterSpacing: '0.02em'
          }}>Evento no encontrado</h2>
          <p style={{
            fontFamily: 'var(--font-ui)',
            color: 'var(--color-text-muted)',
            margin: '0 0 1.5rem 0'
          }}>El evento que buscas no existe o ha sido eliminado.</p>
          <Link 
            to="/eventos" 
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.5rem',
              background: 'var(--ctaBg)',
              color: 'var(--ctaText)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-ui)',
              fontWeight: 'var(--font-weight-bold)',
              textDecoration: 'none',
              boxShadow: 'none',
              border: 'var(--card-border)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Ver todos los eventos
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{evento.seo_title || `${evento.nombre} - Plaza Aurora`}</title>
        <meta name="description" content={evento.seo_description || evento.descripcion || `Evento ${evento.nombre} en Plaza Aurora Outlet`} />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos/${id}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="event" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos/${id}`} />
        <meta property="og:title" content={evento.seo_title || `${evento.nombre} - Plaza Aurora`} />
        <meta property="og:description" content={evento.seo_description || evento.descripcion || `Evento ${evento.nombre} en Plaza Aurora Outlet`} />
        <meta property="og:image" content={evento.og_image_url || evento.imagen_url || ''} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={evento.seo_title || `${evento.nombre} - Plaza Aurora`} />
        <meta name="twitter:description" content={evento.seo_description || evento.descripcion || `Evento ${evento.nombre} en Plaza Aurora Outlet`} />
        <meta name="twitter:image" content={evento.og_image_url || evento.imagen_url || ''} />
        
        {/* Robots */}
        <meta name="robots" content={evento.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>
      
      {/* Breadcrumb JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Eventos",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": evento.nombre,
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos/${id}`
            }
          ]
        })}
      </script>
      
      {/* Event JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          "name": evento.nombre,
          "url": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos/${id}`,
          "image": evento.imagen_url || undefined,
          "description": evento.descripcion || `Evento ${evento.nombre} en Plaza Aurora Outlet`,
          "startDate": evento.fecha_inicio || undefined,
          "endDate": evento.fecha_fin || evento.fecha_inicio || undefined,
          "eventStatus": evento.estado === 'Activo' ? "https://schema.org/EventScheduled" : evento.estado === 'Finalizado' ? "https://schema.org/EventPostponed" : "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "Plaza Aurora",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Av. Carlos Izaguirre 271-275",
              "addressLocality": "Independencia",
              "addressRegion": "Lima",
              "postalCode": "15113",
              "addressCountry": "PE"
            }
          },
          "organizer": {
            "@type": "Organization",
            "name": "Plaza Aurora",
            "url": import.meta.env.VITE_SITE_URL || "https://www.plazaaurora.pe"
          }
        })}
      </script>
      
      {/* JSON-LD Structured Data */}
      {(evento.json_ld || evento.fecha_inicio) && (
        <script type="application/ld+json">
          {evento.json_ld || JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": evento.nombre,
            "description": evento.descripcion || `Evento ${evento.nombre} en Plaza Aurora Outlet`,
            "image": evento.imagen_url || '',
            "startDate": evento.fecha_inicio || '',
            "endDate": evento.fecha_fin || evento.fecha_inicio || '',
            "eventStatus": evento.estado === 'Activo' ? 'https://schema.org/EventScheduled' : evento.estado === 'Finalizado' ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "location": {
              "@type": "Place",
              "name": "Plaza Aurora Outlet",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Av. Carlos Izaguirre 271-275",
                "addressLocality": "Independencia",
                "addressRegion": "Lima",
                "addressCountry": "PE"
              }
            },
            "organizer": {
              "@type": "Organization",
              "name": "Plaza Aurora Outlet",
              "url": import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'
            },
            "url": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/eventos/${id}`
          })}
        </script>
      )}
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Breadcrumb */}
        <nav style={{
          marginBottom: '1.5rem',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-small)'
        }}>
          <Link 
            to="/" 
            style={{
              color: 'var(--color-text-primary)',
              textDecoration: 'none',
              transition: 'text-decoration 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Inicio
          </Link>
          <span style={{ margin: '0 0.5rem', color: 'var(--color-text-muted)' }}>/</span>
          <Link 
            to="/eventos" 
            style={{
              color: 'var(--color-text-primary)',
              textDecoration: 'none',
              transition: 'text-decoration 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Eventos
          </Link>
          <span style={{ margin: '0 0.5rem', color: 'var(--color-text-muted)' }}>/</span>
          <span style={{ color: 'var(--color-text-muted)' }}>{evento.nombre}</span>
        </nav>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth >= 1024 ? '2fr 1fr' : '1fr',
          gap: '2rem'
        }}>
          {/* Columna Principal */}
          <div>
            {/* Imagen Principal */}
            <div style={{
              position: 'relative',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: 'var(--color-accent-gray)',
              marginBottom: '1.5rem'
            }}>
              {evento.imagen_url ? (
                <img
                  src={evento.imagen_url}
                  alt={evento.nombre}
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #E0E7FF 0%, #DBEAFE 100%)'
                }}>
                  <svg style={{ width: '96px', height: '96px', color: '#93C5FD' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              
              {/* Badges */}
              <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                <span style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-small)',
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 'var(--font-weight-bold)',
                  background: getEstadoBadgeColor(evento.estado).bg,
                  color: getEstadoBadgeColor(evento.estado).color
                }}>
                  {evento.estado}
                </span>
              </div>
              
              {evento.etiqueta && (
                <div style={{ position: 'absolute', top: '1rem', left: '1rem' }}>
                  <span style={{
                    background: '#F97316',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 'var(--font-size-small)',
                    fontFamily: 'var(--font-ui)',
                    fontWeight: 'var(--font-weight-bold)'
                  }}>
                    {evento.etiqueta}
                  </span>
                </div>
              )}
            </div>

            {/* Título y Categoría */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-display-l)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                margin: '0 0 0.75rem 0',
                lineHeight: '1.1',
                letterSpacing: '0.02em'
              }}>{evento.nombre}</h1>
              {evento.categorias && (
                <span style={{
                  display: 'inline-block',
                  background: '#DBEAFE',
                  color: '#1E40AF',
                  padding: '0.5rem 1rem',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-small)',
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  {evento.categorias.nombre}
                </span>
              )}
            </div>

            {/* Descripción */}
            {evento.descripcion && (
              <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'none',
                border: 'var(--card-border)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-h3)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0',
                  letterSpacing: '0.02em'
                }}>DESCRIPCIÓN</h2>
                <p style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-body)',
                  color: 'var(--color-text-primary)',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-line',
                  margin: 0
                }}>
                  {evento.descripcion}
                </p>
              </div>
            )}

            {/* Marcas Participantes */}
            {evento.eventos_tiendas && evento.eventos_tiendas.length > 0 && (
              <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'none',
                border: 'var(--card-border)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--h2)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>Marcas Participantes</h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '1rem'
                }}>
                  {evento.eventos_tiendas.map((et) => {
                    const tienda = et.tiendas
                    if (!tienda) return null

                    const logoContent = tienda.logo_url ? (
                      <img
                        src={tienda.logo_url}
                        alt={tienda.nombre}
                        style={{
                          maxHeight: '64px',
                          objectFit: 'contain',
                          maxWidth: '100%'
                        }}
                      />
                    ) : (
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--color-text-primary)',
                        textAlign: 'center'
                      }}>
                        {tienda.nombre}
                      </span>
                    )

                    const cardStyle = {
                      background: 'var(--background)',
                      borderRadius: 'var(--radius-md)',
                      padding: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      boxShadow: 'none',
                      border: 'var(--card-border)',
                    }

                    // If tienda is published, render as clickable Link
                    if (tienda.publicado) {
                      return (
                        <Link
                          key={tienda.id}
                          to={`/marcas/${tienda.id}`}
                          style={cardStyle}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                        >
                          {logoContent}
                        </Link>
                      )
                    }

                    // Otherwise, render as non-clickable div (draft participant)
                    return (
                      <div
                        key={tienda.id}
                        style={{
                          ...cardStyle,
                          cursor: 'default',
                          opacity: 0.7
                        }}
                      >
                        {logoContent}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Columna Lateral */}
          <div style={{
            alignSelf: 'flex-start',
            position: window.innerWidth >= 1024 ? 'sticky' : 'static',
            top: window.innerWidth >= 1024 ? '6rem' : 'auto'
          }}>
            {/* Información del Evento */}
            <div style={{
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'none',
              border: 'var(--card-border)',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--h2)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                margin: '0 0 1rem 0'
              }}>Información del Evento</h2>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {/* Fechas */}
                {evento.fecha_inicio && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem'
                    }}>
                      <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>Fecha de inicio:</span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      marginLeft: '1.75rem',
                      margin: 0
                    }}>{formatDate(evento.fecha_inicio)}</p>
                  </div>
                )}

                {evento.fecha_fin && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem'
                    }}>
                      <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>Fecha de fin:</span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      marginLeft: '1.75rem',
                      margin: 0
                    }}>{formatDate(evento.fecha_fin)}</p>
                  </div>
                )}

                {/* Horario */}
                {evento.horario && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem'
                    }}>
                      <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>Horario:</span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      marginLeft: '1.75rem',
                      margin: 0
                    }}>{evento.horario}</p>
                  </div>
                )}

                {/* Lugar */}
                {evento.lugar && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem'
                    }}>
                      <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>Lugar:</span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      marginLeft: '1.75rem',
                      margin: 0
                    }}>{evento.lugar}</p>
                  </div>
                )}

                {/* Piso */}
                {evento.piso && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem'
                    }}>
                      <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>Piso:</span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      marginLeft: '1.75rem',
                      margin: 0
                    }}>{evento.piso}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Formulario de Inscripción */}
            {evento.confirmar_asistencia && evento.estado !== 'Finalizado' && (
              <div style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'none',
                border: 'var(--card-border)',
                padding: '1.5rem'
              }}>
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--h2)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>Inscríbete al Evento</h2>
                
                {success && (
                  <div style={{
                    background: 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)',
                    border: '1px solid #6EE7B7',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-small)',
                      color: '#065F46',
                      margin: 0
                    }}>¡Te has inscrito exitosamente!</p>
                  </div>
                )}

                {error && (
                  <div style={{
                    background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                    border: '1px solid #FCA5A5',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-small)',
                      color: '#DC2626',
                      margin: 0
                    }}>{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--color-text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      {...register('usuario_nombre')}
                      placeholder="Tu nombre completo"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--mutedStroke)',
                        borderRadius: 'var(--radius-md)',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        color: 'var(--color-text-primary)',
                        background: 'var(--surface)',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.outline = 'none'
                        e.currentTarget.style.borderColor = 'var(--accentPurple)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    />
                    {errors.usuario_nombre && (
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        color: '#DC2626',
                        margin: '0.25rem 0 0 0'
                      }}>{errors.usuario_nombre.message}</p>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--color-text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('usuario_email')}
                      placeholder="tu@email.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--mutedStroke)',
                        borderRadius: 'var(--radius-md)',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        color: 'var(--color-text-primary)',
                        background: 'var(--surface)',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.outline = 'none'
                        e.currentTarget.style.borderColor = 'var(--accentPurple)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    />
                    {errors.usuario_email && (
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        color: '#DC2626',
                        margin: '0.25rem 0 0 0'
                      }}>{errors.usuario_email.message}</p>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--color-text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      {...register('usuario_whatsapp')}
                      placeholder="999999999"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--mutedStroke)',
                        borderRadius: 'var(--radius-md)',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-body)',
                        color: 'var(--color-text-primary)',
                        background: 'var(--surface)',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.outline = 'none'
                        e.currentTarget.style.borderColor = 'var(--accentPurple)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    />
                    {errors.usuario_whatsapp && (
                      <p style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        color: '#DC2626',
                        margin: '0.25rem 0 0 0'
                      }}>{errors.usuario_whatsapp.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1.5rem',
                      background: submitting ? 'var(--color-text-muted)' : 'var(--ctaBg)',
                      color: 'var(--ctaText)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      fontWeight: 'var(--font-weight-bold)',
                      border: 'none',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      boxShadow: 'none',
                      border: 'var(--card-border)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                        e.currentTarget.style.background = 'var(--brand-blue)'
                        e.currentTarget.style.color = 'white'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.background = 'var(--ctaBg)'
                        e.currentTarget.style.color = 'var(--ctaText)'
                      }
                    }}
                    onMouseDown={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                        e.currentTarget.style.background = 'var(--brand-blue-600)'
                      }
                    }}
                  >
                    {submitting ? 'Inscribiendo...' : 'Inscribirme'}
                  </button>
                </form>
              </div>
            )}

            {evento.estado === 'Finalizado' && (
              <div style={{
                background: 'var(--background)',
                border: '1px solid var(--mutedStroke)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem'
              }}>
                <p style={{
                  fontFamily: 'var(--font-ui)',
                  color: 'var(--color-text-muted)',
                  textAlign: 'center',
                  margin: 0
                }}>
                  Este evento ha finalizado
                </p>
              </div>
            )}
          </div>
        </div>

        {/* FAQs Section */}
        {faqs.length > 0 && (
          <div style={{ marginTop: '3rem', maxWidth: '1200px', margin: '3rem auto 0' }}>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-display-l)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>Preguntas Frecuentes sobre {evento.nombre}</h2>
            
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '3rem'
            }}>
              {faqs.map((faq) => (
                <details key={faq.id} style={{
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  border: '1px solid var(--color-stroke)',
                  cursor: 'pointer'
                }}>
                  <summary style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h4)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {faq.pregunta}
                    <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                  </summary>
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)',
                    lineHeight: '1.6',
                    marginTop: '1rem',
                    whiteSpace: 'pre-line'
                  }}>
                    {faq.respuesta}
                  </p>
                </details>
              ))}
            </div>
            
            {/* FAQPage JSON-LD */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.pregunta,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.respuesta
                  }
                }))
              })}
            </script>
          </div>
        )}
      </div>
    </>
  )
}


